import React, {
  useCallback, useEffect, useMemo, useState, useRef,
} from 'react';
import * as Styles from './AiBuilderWithTemplate.styles';
import {
  Button,
  ChipCheckbox, IconButton, IconChevron, IconImageError, IconSearch, Pagination, Skeleton,
  TextField,
} from 'gatorcomponents';
import { TemplateCard } from './components/TemplateCard';
import { LoadingContent } from '../LoadingScreen';
import { ContinueWithTemplateModal } from './components/ContinueWithTemplateModal';
import circlesImage from '@/media/onboarding/aiBuilder/circles-to-background.png';
import starImage from '@/media/onboarding/aiBuilder/star-to-background.png';
import { GenericError } from '../GenericError';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { ATIVACAO_FILTER_BRIZY_TEMPLATES } from '@/config/GrowthBook/constants';

const AiBuilderWithTemplate = ({
  locales,
  createSiteState,
  builderProvisioningLoading,
  redirectSuccessPageAiBuilder,
  getTemplateCategories,
  getTemplates,
  createWithTemplatesState,
  openTemplate,
  onGoBack,
  trackingEvents,
  handleSearchTerm,
  handleOnClickClear,
  handleBackToDefault,
}) => {
  const withTypedFilted = useFeatureIsOn(ATIVACAO_FILTER_BRIZY_TEMPLATES);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedPage, setSelectedPage] = useState(0);
  const [selectedCategories, setSelectedCategories] = useState(
    locales.categoriesList[0],
  );
  const [typedTerm, setTypedTerm] = useState('');

  const chipsScrollRef = useRef(null);
  const [showScrollControls, setShowScrollControls] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const { templatesCategories: templatesCategoriesState } = createWithTemplatesState || {};
  const { templatesList: templatesListState } = createWithTemplatesState || {};

  const templatesList = templatesListState.list;
  const templatesPagination = templatesListState.pagination || {};

  const categoriesListState = useMemo(
    () => templatesCategoriesState.list || [],
    [templatesCategoriesState.list],
  );

  const formattedCategories = categoriesListState.map((categoryState) => {
    const localeItem = locales.categoriesList.find(
      categoryLocale => categoryLocale.value === categoryState.slug,
    );

    if (localeItem) {
      return {
        value: categoryState.slug,
        label: localeItem.label,
        id: categoryState.id,
      };
    }

    return null;
  }).filter(Boolean);

  const handleGoBack = () => {
    onGoBack && onGoBack();
  };

  const handleSelectCategory = (e) => {
    setSelectedPage(0);
    setSelectedCategories(e);
  };

  const handleSetSelectedPage = (page) => {
    setSelectedPage(page);
    window.scrollTo({ top: 0 });
  };

  const checkScrollControls = useCallback(() => {
    if (chipsScrollRef.current) {
      const scrollContainer = chipsScrollRef.current;
      const hasOverflow = scrollContainer.scrollWidth > scrollContainer.clientWidth;
      setShowScrollControls(hasOverflow);

      if (hasOverflow) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
        const maxScrollLeft = scrollWidth - clientWidth;

        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < maxScrollLeft);
      } else {
        setCanScrollLeft(false);
        setCanScrollRight(false);
      }
    }
  }, []);

  const handleScrollChips = useCallback((direction) => {
    if (chipsScrollRef.current) {
      const scrollContainer = chipsScrollRef.current;
      const containerWidth = scrollContainer.clientWidth;
      const scrollAmount = Math.max(containerWidth * 0.7, 200);

      if (direction === 'left' && canScrollLeft) {
        scrollContainer.scrollBy({
          left: -scrollAmount,
          behavior: 'smooth',
        });
      } else if (direction === 'right' && canScrollRight) {
        scrollContainer.scrollBy({
          left: scrollAmount,
          behavior: 'smooth',
        });
      }

      setTimeout(() => {
        checkScrollControls();
      }, 300);
    }
  }, [canScrollLeft, canScrollRight, checkScrollControls]);

  const getDefaultData = useCallback(() => {
    getTemplateCategories && getTemplateCategories();
  }, [getTemplateCategories]);

  useEffect(() => {
    getDefaultData();
  }, [getDefaultData]);

  const requestTemplates = useCallback(() => {
    getTemplates && getTemplates({ category: selectedCategories.id, page: selectedPage + 1 });
  }, [selectedCategories, selectedPage, getTemplates]);

  const GenericErrorProps = useCallback(() => ({
    icon: (<IconImageError
      color="primaryExtraDark"
      secondaryColor="attentionLight"
      size="xl"
      testId="IconImageErrorTestId"
    />),
    title: locales.genericError.title,
    buttonLabel: locales.genericError.buttonLabel,
    loading: templatesListState.loading,
    requestTemplates,
  }), [requestTemplates, locales.genericError.title, locales.genericError.buttonLabel, templatesListState.loading]);

  useEffect(() => {
    requestTemplates();
  }, [requestTemplates]);

  useEffect(() => {
    checkScrollControls();
    window.addEventListener('resize', checkScrollControls);

    const scrollContainer = chipsScrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkScrollControls);
    }

    return () => {
      window.removeEventListener('resize', checkScrollControls);
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', checkScrollControls);
      }
    };
  }, [checkScrollControls, formattedCategories]);

  const renderCategoriesList = useCallback(() => {
    if (templatesCategoriesState.loading) {
      // eslint-disable-next-line react/no-array-index-key
      return Array.from({ length: 7 }).map((_, index) => <Skeleton key={`skeleton-${index}`} variant="button" />);
    }

    return formattedCategories.map(category => (
      <Styles.ChipContainer
        key={category.value}
        $checked={selectedCategories.value === category.value}
      >
        <ChipCheckbox
          id={category.id}
          checked={selectedCategories.value === category.value}
          label={category.label}
          value={category.value}
          onMark={handleSelectCategory}
        />
      </Styles.ChipContainer>
    ));
  }, [
    formattedCategories,
    selectedCategories.value,
    templatesCategoriesState.loading,
  ]);

  const handleOpenTemplate = useCallback(() => {
    openTemplate && openTemplate({ templateId: selectedTemplate.id });
    setSelectedTemplate(null);
  }, [openTemplate, selectedTemplate]);

  const renderTemplatesList = useCallback(() => {
    const openPreview = (url) => {
      trackingEvents && trackingEvents.clickShowPreview();
      window.open(url, '_blank');
    };

    const handleOpenTemplate = (template) => {
      trackingEvents && trackingEvents.clickCreateWithThisTemplate();
      setSelectedTemplate(template);
    };

    if (templatesListState.loading || templatesListState.status === 'failure') {
      // eslint-disable-next-line react/no-array-index-key
      return Array.from({ length: 20 }).map((_, index) => <TemplateCard key={`template-card-loading-${index}`} loading />);
    }

    return templatesList.map(template => !!template.id && (
      <TemplateCard
        testId={`${template.slug}-template-card`}
        key={template.id}
        template={template}
        title={template.title}
        image={template.image}
        openTemplateButtonLabel={locales.openTemplateButtonLabel}
        openPreviewButtonLabel={locales.openPreviewButtonLabel}
        openPreview={() => openPreview(template.preview_url)}
        openTemplate={() => handleOpenTemplate(template)}
      />
    ));
  }, [templatesListState.loading, templatesListState.status, locales, templatesList, trackingEvents]);

  const handleCloseOnSuccess = useCallback(() => {
    if (createSiteState.status === 'success') {
      redirectSuccessPageAiBuilder && redirectSuccessPageAiBuilder();
    }
  }, [createSiteState.status, redirectSuccessPageAiBuilder]);

  useEffect(() => {
    handleCloseOnSuccess();
  }, [handleCloseOnSuccess]);

  const continueWithTemplateModalProps = useCallback(() => {
    const onCloseModal = () => {
      trackingEvents && trackingEvents.clickSearchForAnother();
      setSelectedTemplate(null);
    };

    return {
      openTemplate: handleOpenTemplate,
      locales: locales.continueWithTemplateModal,
      onCloseModal,
      trackingEvents,
    };
  }, [handleOpenTemplate, locales, trackingEvents]);

  const handleSearchTemplates = () => {
    const setCategorieAsDefault = () => setSelectedCategories(locales.categoriesList[0]);
    handleSearchTerm && handleSearchTerm({ term: typedTerm, setCategorieAsDefault });
  };

  const handleClickClear = () => {
    setTypedTerm('');
    handleOnClickClear && handleOnClickClear();
  };

  const backToDefault = () => {
    setSelectedCategories(locales.categoriesList[0]);
    handleBackToDefault && handleBackToDefault();
  };

  if (builderProvisioningLoading || (createSiteState && createSiteState.loading)) {
    return <LoadingContent locales={locales} />;
  }

  if (templatesListState.status === 'failure') {
    return <GenericError {...GenericErrorProps()} />;
  }

  return (
    <Styles.Wrapper data-testid="ai-builder-with-template">
      <Styles.ButtonBackWrapper>
        <IconButton
          testId="ai-builder-with-template-button-back"
          icon="IconArrow"
          onClick={handleGoBack}
          color="lowDark"
          size="sm"
        />
      </Styles.ButtonBackWrapper>

      <Styles.WaveTop>
        <Styles.CirclesImage src={circlesImage} alt="loading-circles" />
        <Styles.StarImage src={starImage} alt="loading-star" />
      </Styles.WaveTop>

      <Styles.InnerContainer>
        <Styles.TopBannerTitle>{locales.title}</Styles.TopBannerTitle>

        {withTypedFilted && (
          <Styles.SearchFieldWrapper>
            <TextField
              label={locales.searchTemplateLabel}
              type="search"
              onClickIcon={handleSearchTemplates}
              onClear={handleClickClear}
              value={typedTerm}
              onChange={e => setTypedTerm(e.target.value)}
            />
          </Styles.SearchFieldWrapper>
        )}

        {(!!templatesList.length || templatesCategoriesState.loading) && (
          <Styles.ChipsWrapper $withTypedFilted={withTypedFilted}>
            {showScrollControls && (
            <Styles.ChipsControls
              $disabled={!canScrollLeft}
              onClick={() => handleScrollChips('left')}
            >
              <IconChevron
                color={canScrollLeft ? 'lowDark' : 'lowLight'}
                direction="left"
                size="md"
                testId="IconChevronLeft"
              />
            </Styles.ChipsControls>
            )}

            <Styles.ChipsScroll ref={chipsScrollRef}>
              <Styles.ChipsInner>
                {renderCategoriesList()}
              </Styles.ChipsInner>
            </Styles.ChipsScroll>

            {showScrollControls && (
            <Styles.ChipsControls
              $disabled={!canScrollRight}
              onClick={() => handleScrollChips('right')}
            >
              <IconChevron
                color={canScrollRight ? 'lowDark' : 'lowLight'}
                direction="right"
                size="md"
                testId="IconChevronRight"
              />
            </Styles.ChipsControls>
            )}
          </Styles.ChipsWrapper>
        )}

        {withTypedFilted && !templatesList.length && (
          <Styles.EmptyStateWrapper>
            <IconSearch />
            <Styles.EmptyStateText>
              {locales.emptyTemplateText}

              <Button size="large" label={locales.emptyTemplateCta} onClick={backToDefault} />
            </Styles.EmptyStateText>
          </Styles.EmptyStateWrapper>
        )}

        <Styles.TemplatesListWrapper>
          <Styles.TemplatesList>{renderTemplatesList()}</Styles.TemplatesList>
        </Styles.TemplatesListWrapper>

        {(!templatesListState.loading && templatesListState.status === 'success') && (
          <Styles.PaginationWrapper>
            <Pagination
              startIn={1}
              pagesAmount={templatesPagination.last || 0}
              page={selectedPage}
              setPage={handleSetSelectedPage}
            />
          </Styles.PaginationWrapper>
        )}
      </Styles.InnerContainer>

      {!!selectedTemplate && <ContinueWithTemplateModal {...continueWithTemplateModalProps()} />}
    </Styles.Wrapper>
  );
};

export default AiBuilderWithTemplate;
